import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function LancashireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Lancashire Pothole Claims | 97,757 Potholes Filled in One Year | Fixtyer"
        description="Lancashire County Council filled 97,757 potholes in 2024/25 — nearly double the 2020/21 figure — and spent more than twice its DfT allocation. Yet RED-condition A and B/C roads hit five-year highs and over 27% of unclassified roads have sat in RED for five straight years. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/lancashire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Lancashire', url: '/councils/lancashire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Lancashire spent more than double its DfT allocation in 2024/25 — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN — Lancashire spent £68m of capital against a £32.5m allocation in 2024/25 — but the rating that matters for your claim is road condition, and Lancashire is AMBER overall. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate. A council that fills 97,757 potholes in a single year is running a network that produces defects faster than any budget can prevent.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road in Lancashire?',
            answer: 'The unclassified network is Lancashire\'s weakest spot. The council\'s own report shows U-roads stuck above 26% RED in every year measured since 2021 — 27.56% in 2025 — meaning more than one in four residential and minor roads has been rated as needing structural resurfacing for five consecutive years. The council also concedes that "there has not been any nationally recognised standard for monitoring the condition of the unclassified network", relying instead on its own video surveys.',
          },
          {
            question: 'Pothole repairs nearly doubled in five years — doesn\'t that show the council is on top of it?',
            answer: 'It shows the opposite. Repairs rose every single year, from 50,466 in 2020/21 to 97,757 in 2024/25 — roughly 268 potholes filled every day. A network producing defects at that accelerating rate is, by definition, one where new potholes routinely form between inspections — exactly the scenario where prior reports and photographic evidence decide claims. The council even admits there are "areas where we have to visit repeatedly to fill potholes".',
          },
          {
            question: 'Does Lancashire\'s admission that the network was saturated for six months help my claim?',
            answer: 'Yes. The council\'s own transparency report states the network "was considered saturated for more than 6 months" and that this "contributed to a significant increase in pothole damage". That is documented knowledge of accelerated, county-wide deterioration. Under Section 58, knowledge of elevated risk raises the bar for what a reasonable inspection and repair regime looks like during and after that period.',
          },
          {
            question: 'Lancashire says its network has "remained relatively stable" — does that hurt my claim?',
            answer: 'No — test the claim against the council\'s own tables. RED-condition A roads nearly doubled from 2.1% in 2020 to 3.9% in 2025, and RED B/C roads jumped from 4.59% to 6.49% in the final year alone — both five-year highs. Pothole repairs almost doubled over the same period. If a defendant council describes that trajectory as "relatively stable", its own published data is available to put the deterioration on the record.',
          },
          {
            question: 'My pothole was on a C road — how often was it actually surveyed?',
            answer: 'Only partially each year. Lancashire\'s report states that SCANNER surveys cover A and B roads in both directions annually, but C roads "in one direction one year and the opposite direction the next". If your pothole was in the lane that was not machine-surveyed that year, the council\'s condition picture for that exact stretch could be up to two years old — a fair question to put to any Section 58 defence.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Lancashire County Council" 
        councilSlug="lancashire" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/lancashire"
        headline="Lancashire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Lancashire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Lancashire: Double the Funding, 268 Potholes Filled a Day
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Lancashire County Council spent £68m of capital against a £32.5m DfT allocation in 2024/25 
            and earns a GREEN scorecard for spend. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — 
            because RED-condition A and B/C roads hit five-year highs in 2025, over a quarter of 
            unclassified roads have sat in RED for five straight years, and pothole repairs have 
            <strong className="text-rose-400"> nearly doubled to 97,757 in a single year</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">97,757</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              Roughly 268 potholes every single day — up from 50,466 in 2020/21, a rise of 
              almost 94% in five years across Lancashire's 7,035km carriageway network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Six years of SCANNER survey data from Lancashire's own transparency report — 
            and the last two years point firmly downhill
          </p>

          {/* A-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">A roads (809.68km): RED nearly doubled since 2020</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.1%</td>
                    <td className="px-4 py-2 text-center">20.47%</td>
                    <td className="px-4 py-2 text-center">77.43%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.43%</td>
                    <td className="px-4 py-2 text-center">16.8%</td>
                    <td className="px-4 py-2 text-center">81.77%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.2%</td>
                    <td className="px-4 py-2 text-center">19.9%</td>
                    <td className="px-4 py-2 text-center">77.90%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.1%</td>
                    <td className="px-4 py-2 text-center">16.4%</td>
                    <td className="px-4 py-2 text-center">81.50%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">22.5%</td>
                    <td className="px-4 py-2 text-center">74.50%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2025</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.9%</td>
                    <td className="px-4 py-2 text-center font-bold">23.4%</td>
                    <td className="px-4 py-2 text-center font-bold">72.70%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition A roads have nearly doubled since 2020 (2.1% → 3.9%) and good-condition 
              A roads have slid from 81.77% at their 2021 peak to 72.70%. The county's most-inspected, 
              most-trafficked roads are heading the wrong way.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (468.53km): worst RED figure in six years of data</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.36%</td>
                    <td className="px-4 py-2 text-center">26.38%</td>
                    <td className="px-4 py-2 text-center">69.23%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.59%</td>
                    <td className="px-4 py-2 text-center">24.59%</td>
                    <td className="px-4 py-2 text-center">71.82%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.65%</td>
                    <td className="px-4 py-2 text-center">24.3%</td>
                    <td className="px-4 py-2 text-center">72.04%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4.44%</td>
                    <td className="px-4 py-2 text-center">24.65%</td>
                    <td className="px-4 py-2 text-center">70.91%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center">4.59%</td>
                    <td className="px-4 py-2 text-center">25.73%</td>
                    <td className="px-4 py-2 text-center">69.68%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2025</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.49%</td>
                    <td className="px-4 py-2 text-center font-bold">29.30%</td>
                    <td className="px-4 py-2 text-center font-bold">64.21%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              The single-year jump from 4.59% to 6.49% RED between 2024 and 2025 is the sharpest 
              deterioration in the whole dataset — a rise of over 40% in twelve months. More than a third 
              of the B/C network (35.79%) is now in RED or AMBER condition.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£32.5m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£68.1m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">41%</div>
                <div className="text-brand-300 text-sm">Estimated reactive share of spend</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white mb-4">
              Lancashire spent more than <span className="text-emerald-400">double its DfT allocation</span> in 
              2024/25 — and RED-condition roads still hit five-year highs in 2025. The problem isn't the 
              chequebook. The network is deteriorating faster than even a GREEN-rated spend programme can repair it.
            </p>
            <p className="text-brand-300 text-sm">
              Note the reactive share: 39-45% of spend every year since 2020/21 has gone on reacting to 
              defects rather than preventing them. The 2025/26 projection of £71.8m includes £15m of 
              emergency top-up approved at full council plus £9.9m to complete prior-year schemes — 
              catch-up money, by the council's own footnote.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road problem */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            One In Four Unclassified Roads: RED, Five Years Running
          </h2>
          <p className="text-surface-600 mb-8">
            The residential and minor-road network has been rated as needing structural resurfacing 
            at over 26% in every single year measured
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">No data published (NA)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26.39%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">27.65%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">28.14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">27.67%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">27.56%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">No National Standard — By The Council's Own Admission</h3>
              <p className="text-surface-600 text-sm mb-3">
                Lancashire's report states plainly: "There has not been any nationally recognised standard 
                for monitoring the condition of the unclassified network." Instead the council grades U-roads 
                from its own detailed video surveys, on a one-to-five scale from "Free from Defects" to 
                "Structurally Impaired".
              </p>
              <p className="text-surface-600 text-sm">
                RED is defined by the council as the area of damage in grades 4 and 5 — roads considered 
                for full resurfacing. By that measure, <strong>more than one in four unclassified roads has 
                needed structural work in every year measured</strong>, with no improvement between 2021 and 2025.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">"Relatively Stable" — Stable At A Bad Level</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council's verdict on this data is that "the unclassified network condition has remained 
                relatively stable over the last few years." That is true — and it is precisely the problem. 
                Stability at 27-28% RED means the backlog of structurally impaired residential roads has 
                not been cleared, despite the TAMP's Phase 2 (2020-2025) explicitly targeting the 
                unclassified network.
              </p>
              <p className="text-amber-800 text-sm">
                Phase 2 has now ended. The council says Phase 3 priorities are still "being reviewed", with 
                the interim 2025/26 focus shifted back to A, B and C roads — leaving U-roads waiting again.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for 
              knowing and acting on the condition of its roads. For Lancashire's network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• If 27%+ of U-roads have needed structural resurfacing for five straight years, what was done about yours?</li>
              <li>• C roads are machine-surveyed "in one direction one year and the opposite direction the next" — was your lane surveyed the year you hit the pothole?</li>
              <li>• A five-year programme dedicated to unclassified roads ended in March 2025 with RED still above 27% — was the system actually working?</li>
              <li>• With no nationally recognised standard for U-road condition, how does the council prove its video-survey grading caught your defect's road?</li>
            </ul>
            <p className="text-white font-medium">
              A defect on a road the council's own data has flagged as structurally impaired — year after 
              year — is very hard to dismiss as unforeseeable.
            </p>
          </div>
        </div>
      </section>

      {/* The Pothole Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            352,885 Potholes in Five Years — And Accelerating
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own estimates show repairs rising every single year — up almost 94% since 2020/21
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (council estimate)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change on previous year</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">50,466</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">58,125</td>
                  <td className="px-6 py-4 text-center text-rose-600">+15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">62,395</td>
                  <td className="px-6 py-4 text-center text-rose-600">+7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold">84,142</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+35%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">97,757</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+16%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">352,885</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+94% over the period</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~268 Potholes a Day in 2024/25</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Lancashire fills around 193 potholes per day — rising to roughly 
                268 a day in 2024/25. A network producing potholes at that accelerating rate is, by 
                definition, one where defects routinely form between inspections — exactly the scenario 
                where prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Repeat-Visit Admission</h3>
              <p className="text-amber-800 text-sm">
                The council's own words: it is "investing in larger scale patching to ensure that the areas 
                where we have to visit repeatedly to fill potholes are in good order." That is an 
                acknowledgement that parts of the network generate the same potholes again and again — 
                if your defect was in one of those repeat-visit areas, the council knew the location was a problem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Saturation Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Six-Month Saturation Admission
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own explanation for the surge in pothole damage — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Consequently, the highway network was consistently wet from the summer of 2024 through to the winter season. The network was considered saturated for more than 6 months."
              source="Lancashire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard 
              quote="This contributed to a significant increase in pothole damage and was further exacerbated by some periods of cold weather over winter."
              source="Lancashire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Lancashire formally acknowledges that rainfall running at around 125% of the long-term 
                average — up to 135% in some areas — left the network saturated for over half a year, and 
                that this caused "a significant increase in pothole damage". That is documented, county-wide 
                knowledge of accelerated deterioration.
              </p>
              <p className="text-surface-600 text-sm">
                Knowledge of accelerated wear raises the standard for what a "reasonable" inspection and 
                repair regime looks like during and after that period under Section 58.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Did the council increase inspection frequency during the saturation period it has documented?</li>
                <li>• If pothole damage was known to be surging, why wasn't your defect caught or made safe?</li>
                <li>• Was your area one of those experiencing rainfall at 135% of the long-term average?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Big-Spending AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Lancashire invests heavily and innovates — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — spent more than double its DfT allocation in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual SCANNER surveys on the classified network, plus video surveys of U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Long-standing asset management plan (TAMP) with a preventative-first philosophy since 2014
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Genuine innovation — AI defect identification, recycled materials, first-time permanent fixes
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence, especially on A roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — RED A roads and B/C roads both at five-year highs in 2025
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — unlike Buckinghamshire, the DfT did not rate Lancashire GREEN here
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 27%+ of U-roads in RED for five consecutive years, with no nationally recognised survey standard
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 97,757 potholes filled in one year — repairs rising every year since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted six-month network saturation causing "a significant increase in pothole damage"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> C roads machine-surveyed in only one direction per year
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN spend scorecard, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching from repeat visits)</li>
              <li>• The road's class — on a U-road, five years of 27%+ RED is your strongest structural argument; on a C road, ask which direction was surveyed and when</li>
              <li>• The date — incidents during or after the documented 2024 saturation period sit squarely inside the council's own admission of surging damage</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Lancashire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Lancashire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A big-spending council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road RED-backlog argument</li>
                <li>• No prior-report search</li>
                <li>• No saturation-period analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Five-year RED-condition trends documented</li>
                <li>✅ C-road one-direction survey gap argued</li>
                <li>✅ 97,757 potholes in one year cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Lancashire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=lancashire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Lancashire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Lancashire spent more than double its DfT allocation in 2024/25 — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN — Lancashire spent £68m of capital against a £32.5m 
                allocation in 2024/25 — but the rating that matters for your claim is road condition, and 
                Lancashire is AMBER overall. Section 58 turns on whether the specific defect that damaged 
                your vehicle was reasonably inspected and repaired, not on how much the council spends in 
                aggregate. A council that fills 97,757 potholes in a single year is running a network that 
                produces defects faster than any budget can prevent.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road in Lancashire?
              </h3>
              <p className="text-surface-600 text-sm">
                The unclassified network is Lancashire's weakest spot. The council's own report shows U-roads 
                stuck above 26% RED in every year measured since 2021 — 27.56% in 2025 — meaning more than 
                one in four residential and minor roads has been rated as needing structural resurfacing for 
                five consecutive years. The council also concedes that "there has not been any nationally 
                recognised standard for monitoring the condition of the unclassified network", relying 
                instead on its own video surveys.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs nearly doubled in five years — doesn't that show the council is on top of it?
              </h3>
              <p className="text-surface-600 text-sm">
                It shows the opposite. Repairs rose every single year, from 50,466 in 2020/21 to 97,757 in 
                2024/25 — roughly 268 potholes filled every day. A network producing defects at that 
                accelerating rate is, by definition, one where new potholes routinely form between 
                inspections — exactly the scenario where prior reports and photographic evidence decide 
                claims. The council even admits there are "areas where we have to visit repeatedly to fill 
                potholes".
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Lancashire's admission that the network was saturated for six months help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The council's own transparency report states the network "was considered saturated for 
                more than 6 months" and that this "contributed to a significant increase in pothole damage". 
                That is documented knowledge of accelerated, county-wide deterioration. Under Section 58, 
                knowledge of elevated risk raises the bar for what a reasonable inspection and repair regime 
                looks like during and after that period.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Lancashire says its network has "remained relatively stable" — does that hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — test the claim against the council's own tables. RED-condition A roads nearly doubled 
                from 2.1% in 2020 to 3.9% in 2025, and RED B/C roads jumped from 4.59% to 6.49% in the final 
                year alone — both five-year highs. Pothole repairs almost doubled over the same period. If a 
                defendant council describes that trajectory as "relatively stable", its own published data is 
                available to put the deterioration on the record.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                My pothole was on a C road — how often was it actually surveyed?
              </h3>
              <p className="text-surface-600 text-sm">
                Only partially each year. Lancashire's report states that SCANNER surveys cover A and B roads 
                in both directions annually, but C roads "in one direction one year and the opposite 
                direction the next". If your pothole was in the lane that was not machine-surveyed that year, 
                the council's condition picture for that exact stretch could be up to two years old — a fair 
                question to put to any Section 58 defence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.lancashire.gov.uk/council/strategies-policies-plans/roads-parking-and-travel/local-highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Lancashire County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
